import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import CssBaseline from '@material-ui/core/CssBaseline';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { DynamicForm, SubmitBtn, Copyright, Info } from '../../components';
import { useAuth, useRoutes, useApi } from '../../hooks';
import { logout } from '../../store';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(5),
  },
  actions: {
    flex: 1,
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
  },
  actionBtn: {
    flex: 1,
  },
  secondBtn: {
    width: `100%`,
    marginTop: theme.spacing(3),
  },
  info: {
    marginTop: theme.spacing(7),
    marginBottom: theme.spacing(5),
  },
  rules: {
    marginLeft: 0,
  },
}));

export const ResetPassword: React.FC = () => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const { auth } = useAuth();
  const { paths, navigate } = useRoutes();
  const { updatePassword } = useApi();
  const navigateToAsct = navigate(paths.asct.path);
  const navigateToLogin = navigate(paths.login.path);
  const dispatch = useDispatch();

  const handleOnSubmit = useCallback(
    async ({ password, newPassword }) => {
      try {
        await updatePassword(auth?.userName, password, newPassword);
        navigateToAsct();
      } catch (error) {
        setError('auth/cannot-reset-password');
      }
    },
    [auth, setError, navigateToAsct, updatePassword]
  );

  const handleOnCancel = useCallback(() => {
    dispatch(logout());
    navigateToLogin();
  }, [dispatch, navigateToLogin]);

  return (
    <div>
      <CssBaseline />
      <Container maxWidth="sm" className={classes.container}>
        <Typography variant="h4" component="h4" align="center">
          Reinitialisation de mot de passe
        </Typography>
        <DynamicForm
          fields={{
            password: {
              name: 'password',
              label: 'Mot de passe provisoire',
              initialValue: '',
              type: 'password',
            },
            newPassword: {
              name: 'newPassword',
              label: 'Nouveau mot de passe',
              initialValue: '',
              type: 'password',
            },
            confirmationPassword: {
              name: 'confirmationPassword',
              label: 'Confirmation mot de passe',
              initialValue: '',
              type: 'password',
            },
          }}
          validationSchema={Yup.object({
            password: Yup.string().required('Ce champ est requis'),
            newPassword: Yup.string()
              .required('Ce champ est requis')
              .min(8, '8 caractères minimum')
              .matches(/.*\d/, ' 1 chiffre minimum')
              .matches(/.*[a-z]/, '1 minuscule minimum')
              .matches(/.*[A-Z]/, '1 majuscule minimum'),
            confirmationPassword: Yup.string()
              .oneOf([Yup.ref('newPassword')], 'Les mots de passe ne se correspondent pas')
              .required('Ce champ est requis'),
          })}
          onSubmit={handleOnSubmit}
          error={error}
        >
          {(form: any) => (
            <div className={classes.actions}>
              <SubmitBtn
                color="primary"
                title="Valider"
                variant="contained"
                className={classes.actionBtn}
                disabled={!form.isValid || form.isSubmitting}
                isSubmitting={form.isSubmitting}
              />
            </div>
          )}
        </DynamicForm>
        <Button color="primary" className={classes.secondBtn} onClick={handleOnCancel}>
          Annuler
        </Button>
        <div className={classes.info}>
          <Info
            title="Votre nouveau mot de passe doit être constitué de : "
            subtitle={
              <ul className={classes.rules}>
                <li>8 Caractères minimum</li>
                <li>1 majuscule minimum</li>
                <li>1 minuscule minimum</li>
                <li>1 chiffre minimum </li>
              </ul>
            }
          />
        </div>
        <Copyright />
      </Container>
    </div>
  );
};

export default ResetPassword;
